import { GlobalDto } from '../global/globalReducerTypes';
import { ConfigurationType } from '@server/parser';

export interface DocumentationItemsMap {
  [ConfigurationType.recordTypes]: string[];
  [ConfigurationType.fields]: string[];
  [ConfigurationType.validationRules]: string[];
  [ConfigurationType.approvalProcesses]: string[];
  [ConfigurationType.workflowRules]: string[];
  [ConfigurationType.processBuilderFlows]: string[];
  [ConfigurationType.flows]: string[];
  [ConfigurationType.apexTriggers]: string[];
  [ConfigurationType.apexClasses]: string[];
  [ConfigurationType.cpqData]: string[];
  [ConfigurationType.layouts]: string[];
  [ConfigurationType.sweepAutomation]: string[];
  [ConfigurationType.sweepAlert]: string[];
  [ConfigurationType.sweepAssignment]: string[];
  [ConfigurationType.sweepScheduledAssignment]: string[];
  [ConfigurationType.sweepDedupe]: string[];
  [ConfigurationType.sweepMatching]: string[];
}

// Generic type for objects that have an id property
interface Identifiable {
  id?: string;
  isActive?: boolean;
}

// Configuration types that are part of globalDto.documentation
const DOCUMENTATION_CONFIGURATION_MAPPING = {
  recordTypes: ConfigurationType.recordTypes,
  fields: ConfigurationType.fields,
  validationRules: ConfigurationType.validationRules,
  approvalProcesses: ConfigurationType.approvalProcesses,
  workflowRules: ConfigurationType.workflowRules,
  processBuilderFlows: ConfigurationType.processBuilderFlows,
  flows: ConfigurationType.flows,
  apexTriggers: ConfigurationType.apexTriggers,
  apexClasses: ConfigurationType.apexClasses,
  cpqData: ConfigurationType.cpqData,
  layouts: ConfigurationType.layouts,
} as const;

// Configuration types that are direct properties of globalDto
const ROOT_LEVEL_CONFIGURATION_MAPPING = {
  automations: { targetKey: ConfigurationType.sweepAutomation, idProperty: 'automationId' },
  alerts: { targetKey: ConfigurationType.sweepAlert, idProperty: 'automationId' },
  assignments: { targetKey: ConfigurationType.sweepAssignment, idProperty: 'automationId' },
  scheduledAssignments: {
    targetKey: ConfigurationType.sweepScheduledAssignment,
    idProperty: 'automationId',
  },
  dedupe: { targetKey: ConfigurationType.sweepDedupe, idProperty: 'automationId' },
  matching: { targetKey: ConfigurationType.sweepMatching, idProperty: 'automationId' },
  rollups: { targetKey: ConfigurationType.fields, idProperty: 'rollupId' }, // Rollups are assigned to fields category
} as const;

/**
 * Extracts IDs from an array of objects that have an optional id property and are not explicitly inactive
 */
const extractIds = <T extends Identifiable>(items: T[] | undefined): string[] => {
  if (!items?.length) return [];
  return items.filter((item) => item.id && item.isActive !== false).map((item) => item.id!);
};

/**
 * Extracts IDs from sweep elements using the correct ID property and are not explicitly inactive
 */
const extractSweepElementIds = (items: any[] | undefined, idProperty: string): string[] => {
  if (!items?.length) return [];
  return items
    .filter((item) => item[idProperty] && item.isActive !== false)
    .map((item) => item[idProperty]);
};

/**
 * Extracts IDs from recordTypes using object.name and recordType.name
 */
const extractRecordTypeIds = (objectData: any): string[] => {
  if (!objectData.recordTypes?.length || !objectData.name) return [];
  return objectData.recordTypes
    .filter((recordType: any) => recordType.name && recordType.isActive !== false)
    .map((recordType: any) => `${objectData.name}.${recordType.name}`);
};

/**
 * Processes a single object data item and extracts IDs by configuration type
 */
const processDocumentationObjectData = (objectData: any): Partial<DocumentationItemsMap> => {
  const result: Partial<DocumentationItemsMap> = {};

  // Special handling for recordTypes
  const recordTypeIds = extractRecordTypeIds(objectData);
  if (recordTypeIds.length > 0) {
    result[ConfigurationType.recordTypes] = recordTypeIds;
  }

  // Process other configuration types from documentation objects (excluding recordTypes)
  Object.entries(DOCUMENTATION_CONFIGURATION_MAPPING).forEach(([sourceKey, targetKey]) => {
    if (sourceKey === 'recordTypes') return; // Skip recordTypes as it's handled above

    const ids = extractIds(objectData[sourceKey]);
    if (ids.length > 0) {
      result[targetKey as keyof DocumentationItemsMap] = ids;
    }
  });

  return result;
};

/**
 * Processes root-level configuration types from globalDto
 */
const processRootLevelConfiguration = (globalDto: GlobalDto): Partial<DocumentationItemsMap> => {
  const result: Partial<DocumentationItemsMap> = {};

  // Process configuration types that are direct properties of globalDto
  Object.entries(ROOT_LEVEL_CONFIGURATION_MAPPING).forEach(([sourceKey, config]) => {
    const { targetKey, idProperty } = config;
    const ids = extractSweepElementIds((globalDto as any)[sourceKey], idProperty);
    if (ids.length > 0) {
      if (!result[targetKey as keyof DocumentationItemsMap]) {
        result[targetKey as keyof DocumentationItemsMap] = [];
      }
      (result[targetKey as keyof DocumentationItemsMap] as string[]).push(...ids);
    }
  });

  return result;
};

/**
 * Merges partial results into the main items map
 */
const mergeResults = (
  target: DocumentationItemsMap,
  source: Partial<DocumentationItemsMap>,
): void => {
  Object.entries(source).forEach(([key, ids]) => {
    if (ids && Array.isArray(ids)) {
      (target as any)[key].push(...ids);
    }
  });
};

/**
 * Creates an empty items map with all required properties
 */
const createEmptyItemsMap = (): DocumentationItemsMap => ({
  [ConfigurationType.recordTypes]: [],
  [ConfigurationType.fields]: [],
  [ConfigurationType.validationRules]: [],
  [ConfigurationType.approvalProcesses]: [],
  [ConfigurationType.workflowRules]: [],
  [ConfigurationType.processBuilderFlows]: [],
  [ConfigurationType.flows]: [],
  [ConfigurationType.apexTriggers]: [],
  [ConfigurationType.apexClasses]: [],
  [ConfigurationType.cpqData]: [],
  [ConfigurationType.layouts]: [],
  [ConfigurationType.sweepAutomation]: [],
  [ConfigurationType.sweepAlert]: [],
  [ConfigurationType.sweepAssignment]: [],
  [ConfigurationType.sweepScheduledAssignment]: [],
  [ConfigurationType.sweepDedupe]: [],
  [ConfigurationType.sweepMatching]: [],
});

/**
 * Creates a flat map of item IDs from GlobalDto, organized by configuration type.
 *
 * @param globalDto - The GlobalDto containing documentation and configuration data
 * @returns DocumentationItemsMap with arrays of IDs for each configuration type
 */
export const createDocumentationItemsMap = (globalDto: GlobalDto): DocumentationItemsMap => {
  const itemsMap = createEmptyItemsMap();

  // Process root-level configuration types (sweep elements and rollups)
  const rootLevelResults = processRootLevelConfiguration(globalDto);
  mergeResults(itemsMap, rootLevelResults);

  // Process documentation array for standard configuration types
  if (
    globalDto.documentation.parsedObjects &&
    Array.isArray(globalDto.documentation.parsedObjects)
  ) {
    globalDto.documentation.parsedObjects.forEach((objectData) => {
      const documentationResults = processDocumentationObjectData(objectData);
      mergeResults(itemsMap, documentationResults);
    });
  }

  return itemsMap;
};

/**
 * Generic utility function to create item maps from any data structure
 * @param data - Array of objects containing configuration items
 * @param configMapping - Mapping of source properties to target keys
 * @param additionalProcessors - Optional additional processing functions
 */
export const createGenericItemsMap = <
  T extends Record<string, any>,
  R extends Record<string, string[]>,
>(
  data: T[],
  configMapping: Record<string, keyof R>,
  additionalProcessors?: Array<(item: T) => Partial<R>>,
): R => {
  const result = Object.keys(configMapping).reduce((acc, key) => {
    acc[configMapping[key]] = [] as any;
    return acc;
  }, {} as R);

  data.forEach((item) => {
    // Process main mappings
    Object.entries(configMapping).forEach(([sourceKey, targetKey]) => {
      const ids = extractIds(item[sourceKey]);
      (result[targetKey] as string[]).push(...ids);
    });

    // Apply additional processors if provided
    additionalProcessors?.forEach((processor) => {
      const additionalResults = processor(item);
      Object.entries(additionalResults).forEach(([key, ids]) => {
        if (ids && Array.isArray(ids)) {
          (result[key as keyof R] as string[]).push(...ids);
        }
      });
    });
  });

  return result;
};
